// Drives the embedded external dashboard panel (Grafana/Alertmanager/
// Prometheus — see PlatformWeb.Observability.Dashboards): sizes the frame to
// the viewport, handles fullscreen, and falls back to a manual link when the
// frame doesn't load.
//
// Why the height is measured here instead of a CSS calc(): the chrome above
// the frame (app header, breadcrumb, this page's toolbar, an optional flash
// message) has no fixed total height, and any guess that runs long produces
// a second scrollbar beside the iframe's own — the exact thing this page is
// meant to avoid. Measuring the frame's own top edge is always right.
//
// Why the fallback is a timeout: cross-origin framing refusals
// (X-Frame-Options, CSP frame-ancestors) happen at the browser's render
// step, not the network layer, so the iframe's 'load' event still fires when
// framing was refused, and contentDocument throws for any cross-origin frame
// whether it loaded or not. There is no reliable signal to read, so a frame
// that hasn't loaded within TIMEOUT_MS is assumed stuck (auth redirect loop,
// connection refused). A frame that loads but renders blank is only caught
// by the always-visible "New tab" button in the toolbar.
const TIMEOUT_MS = 6000;

// Breathing room below the frame. The app layout's <main> adds its own
// bottom padding, so leaving less than that here reintroduces a sliver of
// page scroll.
const BOTTOM_GAP = 24;
const FULLSCREEN_GAP = 12;

const MIN_HEIGHT = 320;

const ExternalFrame = {
  mounted() {
    this.frame = this.el.querySelector("[data-role='frame-wrap']");
    this.loading = this.el.querySelector("[data-role='loading']");
    this.error = this.el.querySelector("[data-role='error']");

    this.onResize = () => this.scheduleMeasure();
    this.onFullscreenChange = () => {
      const active = document.fullscreenElement === this.el;
      this.el.style.padding = active ? `${FULLSCREEN_GAP}px` : "";
      this.scheduleMeasure();
    };
    // Delegated rather than bound to the button itself, so the handler
    // survives any LiveView patch that happens to replace that node.
    this.onClick = (event) => {
      if (event.target.closest("[data-role='fullscreen']")) this.toggleFullscreen();
    };

    window.addEventListener("resize", this.onResize);
    document.addEventListener("fullscreenchange", this.onFullscreenChange);
    this.el.addEventListener("click", this.onClick);

    this.arm();
    // Deferred: on first mount the surrounding layout may not have settled,
    // and a top edge measured too early sizes the frame wrong.
    this.scheduleMeasure();
  },

  // Fires on every LiveView patch — notably the kiosk toggle, which swaps the
  // iframe's src. LiveView updates the attribute and the browser reloads the
  // frame, but the previous 'load' listener was spent, so the overlay would
  // stay hidden through the reload without re-arming here.
  updated() {
    this.arm();
    this.scheduleMeasure();
  },

  destroyed() {
    clearTimeout(this.timer);
    cancelAnimationFrame(this.raf);
    window.removeEventListener("resize", this.onResize);
    document.removeEventListener("fullscreenchange", this.onFullscreenChange);
    this.el.removeEventListener("click", this.onClick);
  },

  // Resets to the loading state and waits for whichever comes first: the
  // frame loading, or the timeout. No-ops when the src hasn't changed, so a
  // patch unrelated to the iframe doesn't flash the spinner over a dashboard
  // that is already up.
  arm() {
    const iframe = this.el.querySelector("iframe");
    if (!iframe || iframe.src === this.armedSrc) return;

    this.armedSrc = iframe.src;
    clearTimeout(this.timer);

    if (this.loading) this.loading.style.display = "flex";
    if (this.error) this.error.style.display = "none";
    iframe.classList.add("invisible");

    iframe.addEventListener(
      "load",
      () => {
        clearTimeout(this.timer);
        if (this.loading) this.loading.style.display = "none";
        iframe.classList.remove("invisible");
      },
      { once: true }
    );

    this.timer = setTimeout(() => {
      if (this.loading) this.loading.style.display = "none";
      if (this.error) this.error.style.display = "flex";
    }, TIMEOUT_MS);
  },

  scheduleMeasure() {
    cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(() => this.measure());
  },

  measure() {
    if (!this.frame) return;

    const gap = document.fullscreenElement === this.el ? FULLSCREEN_GAP : BOTTOM_GAP;
    const top = this.frame.getBoundingClientRect().top;
    const height = Math.max(MIN_HEIGHT, window.innerHeight - top - gap);

    this.frame.style.height = `${height}px`;
  },

  toggleFullscreen() {
    if (document.fullscreenElement === this.el) {
      document.exitFullscreen();
    } else {
      // Safari/older WebKit still only expose the prefixed form; ignore the
      // rejection rather than letting an unhandled promise surface, since the
      // panel is perfectly usable without fullscreen.
      const request = this.el.requestFullscreen || this.el.webkitRequestFullscreen;
      if (request) Promise.resolve(request.call(this.el)).catch(() => {});
    }
  }
};

export default ExternalFrame;
